/**
 * PrizeEntry Component
 *
 * Allows players to enter up to 3 prizes during the setup phase.
 * Validates that at least 1 prize is entered and each prize is 1-50 characters.
 * Prizes are synchronized via WebSocket to the server and visible to both players for transparency.
 *
 * Requirements:
 * - 3 text input fields for prize entry
 * - Validate 1-50 character limit per prize
 * - At least 1 prize required to proceed to ready state
 * - Submit button to send prizes to server
 * - Display validation errors
 */

import { useState, useEffect } from 'react';
import { useGameStore } from '../stores/gameStore';
import { sendMessage } from '../services/partyService';
import { COPY } from '../copy';

function PrizeEntry() {
  const myPrizes = useGameStore((state) => state.myPrizes);
  const setMyPrizes = useGameStore((state) => state.setMyPrizes);
  const canSubmitPrizes = useGameStore((state) => state.canSubmitPrizes);

  const [prize1, setPrize1] = useState('');
  const [prize2, setPrize2] = useState('');
  const [prize3, setPrize3] = useState('');
  const [errors, setErrors] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);

  // Load existing prizes when component mounts
  useEffect(() => {
    if (myPrizes.length > 0) {
      setPrize1(myPrizes[0] || '');
      setPrize2(myPrizes[1] || '');
      setPrize3(myPrizes[2] || '');
      setHasSubmitted(true);
    }
  }, [myPrizes]);

  const validatePrizes = () => {
    const newErrors = [];
    const prizes = [prize1, prize2, prize3]
      .map((p) => p.trim())
      .filter((p) => p.length > 0);

    // Must have at least 1 prize
    if (prizes.length === 0) {
      newErrors.push(COPY.prizes.entry.validation.required);
    }

    // Check individual prize length
    [prize1, prize2, prize3].forEach((prize, index) => {
      const trimmed = prize.trim();
      if (trimmed.length > 0) {
        if (trimmed.length > 50) {
          newErrors.push(COPY.prizes.entry.validation.tooLong(index + 1, 50));
        }
      }
    });

    return { isValid: newErrors.length === 0, errors: newErrors, prizes };
  };

  const handleSubmit = async () => {
    setErrors([]);

    if (!canSubmitPrizes()) {
      setErrors([COPY.prizes.entry.validation.wrongPhase]);
      return;
    }

    const { isValid, errors: validationErrors, prizes } = validatePrizes();

    if (!isValid) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);

    try {
      // Update local state immediately
      setMyPrizes(prizes);

      // Send prizes to server
      await sendMessage({
        type: 'SUBMIT_PRIZES',
        prizes: prizes,
      });

      setHasSubmitted(true);
      console.log('[PrizeEntry] Prizes submitted successfully:', prizes);
    } catch (error) {
      console.error('[PrizeEntry] Error submitting prizes:', error);
      setErrors([COPY.prizes.entry.validation.submitFailed]);
    } finally {
      setIsSubmitting(false);
    }
  };

  const canEdit = canSubmitPrizes() && !isSubmitting;

  return (
    <div className="bg-dark-charcoal border-2 border-neon-purple shadow-glow-purple rounded-lg p-6 mb-6">
      <h3 className="text-xl font-bold text-neon-purple text-glow-purple mb-2">
        {COPY.prizes.entry.title}
      </h3>
      <p className="text-sm text-gray-300 mb-4">
        {COPY.prizes.entry.instructions}
      </p>

      <div className="space-y-3 mb-4">
        <div>
          <label
            htmlFor="prize1"
            className="block text-sm font-medium text-neon-cyan mb-1"
          >
            {COPY.prizes.entry.labels.prize1} <span className="text-neon-pink">{COPY.prizes.entry.labels.required}</span>
          </label>
          <input
            id="prize1"
            type="text"
            value={prize1}
            onChange={(e) => setPrize1(e.target.value)}
            disabled={!canEdit}
            maxLength={50}
            className="w-full px-3 py-2 bg-dark-purple border-2 border-neon-cyan/30 text-gray-100 rounded-md focus:outline-none focus:border-neon-cyan focus:shadow-glow-cyan disabled:bg-dark-purple/50 disabled:border-gray-600 disabled:cursor-not-allowed transition-all"
            placeholder={COPY.prizes.entry.placeholders.prize1}
          />
          <p className="text-xs text-gray-400 mt-1">
            {COPY.prizes.entry.characterCounter(prize1.trim().length, 50)}
          </p>
        </div>

        <div>
          <label
            htmlFor="prize2"
            className="block text-sm font-medium text-neon-cyan mb-1"
          >
            {COPY.prizes.entry.labels.prize2}
          </label>
          <input
            id="prize2"
            type="text"
            value={prize2}
            onChange={(e) => setPrize2(e.target.value)}
            disabled={!canEdit}
            maxLength={50}
            className="w-full px-3 py-2 bg-dark-purple border-2 border-neon-cyan/30 text-gray-100 rounded-md focus:outline-none focus:border-neon-cyan focus:shadow-glow-cyan disabled:bg-dark-purple/50 disabled:border-gray-600 disabled:cursor-not-allowed transition-all"
            placeholder={COPY.prizes.entry.placeholders.prize2}
          />
          <p className="text-xs text-gray-400 mt-1">
            {COPY.prizes.entry.characterCounter(prize2.trim().length, 50)}
          </p>
        </div>

        <div>
          <label
            htmlFor="prize3"
            className="block text-sm font-medium text-neon-cyan mb-1"
          >
            {COPY.prizes.entry.labels.prize3}
          </label>
          <input
            id="prize3"
            type="text"
            value={prize3}
            onChange={(e) => setPrize3(e.target.value)}
            disabled={!canEdit}
            maxLength={50}
            className="w-full px-3 py-2 bg-dark-purple border-2 border-neon-cyan/30 text-gray-100 rounded-md focus:outline-none focus:border-neon-cyan focus:shadow-glow-cyan disabled:bg-dark-purple/50 disabled:border-gray-600 disabled:cursor-not-allowed transition-all"
            placeholder={COPY.prizes.entry.placeholders.prize3}
          />
          <p className="text-xs text-gray-400 mt-1">
            {COPY.prizes.entry.characterCounter(prize3.trim().length, 50)}
          </p>
        </div>
      </div>

      {errors.length > 0 && (
        <div className="mb-4 p-3 bg-neon-pink/20 border-2 border-neon-pink rounded-md shadow-glow-pink">
          {errors.map((error, index) => (
            <p key={index} className="text-sm text-neon-pink">
              {error}
            </p>
          ))}
        </div>
      )}

      {hasSubmitted && (
        <div className="mb-4 p-3 bg-neon-green/20 border-2 border-neon-green rounded-md shadow-glow-green">
          <p className="text-sm text-neon-green font-medium">
            {COPY.prizes.entry.success}
          </p>
          {canEdit && (
            <p className="text-xs text-gray-300 mt-1">
              You can update your prizes until you click "Ready to Play"
            </p>
          )}
        </div>
      )}

      <button
        onClick={handleSubmit}
        disabled={!canEdit}
        className="w-full bg-neon-purple hover:bg-neon-pink border-2 border-white/70 hover:border-white/70 disabled:bg-dark-purple/50 disabled:border-gray-600 disabled:cursor-not-allowed text-white font-semibold py-3 px-6 rounded-lg transition-all shadow-glow-purple"
      >
        {isSubmitting
          ? COPY.prizes.entry.buttons.submitting
          : hasSubmitted
            ? COPY.prizes.entry.buttons.update
            : COPY.prizes.entry.buttons.submit}
      </button>

      {!canEdit && hasSubmitted && (
        <p className="text-xs text-gray-400 mt-2 text-center">
          {COPY.prizes.entry.locked}
        </p>
      )}
    </div>
  );
}

export default PrizeEntry;
